import React, { useState } from "react";
import { Share2, Link as LinkIcon, Check, Facebook, Twitter } from "lucide-react";
import { toast } from "sonner";

/** Pinterest icon (SVG) since lucide doesn't export a dedicated one */
function PinterestIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z" />
    </svg>
  );
}

/** Share buttons with native Web Share API fallback to individual networks. */
export default function SocialShare({ url, title, image }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const href = url || (typeof window !== "undefined" ? window.location.href : "");
  const encodedUrl = encodeURIComponent(href);
  const encodedTitle = encodeURIComponent(title || "");
  const encodedImage = encodeURIComponent(image || "");

  const nativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url: href });
      } else {
        setOpen((o) => !o);
      }
    } catch {
      // user cancelled — no toast
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <div className="relative inline-block" data-testid="social-share">
      <button
        onClick={nativeShare}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#6B5158] hover:text-[#2B2127]"
        data-testid="share-btn"
      >
        <Share2 size={14} />
        Share
      </button>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-2 flex items-center gap-1 rounded-md border border-[#E6D4CC] bg-white p-2 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.15)]" data-testid="share-menu">
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
            target="_blank" rel="noopener noreferrer"
            className="rounded-full p-2 text-[#2B2127] hover:bg-[#F1E3DC]"
            aria-label="Share on Facebook"
          >
            <Facebook size={14} />
          </a>
          <a
            href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
            target="_blank" rel="noopener noreferrer"
            className="rounded-full p-2 text-[#2B2127] hover:bg-[#F1E3DC]"
            aria-label="Share on X / Twitter"
          >
            <Twitter size={14} />
          </a>
          <a
            href={`https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedTitle}`}
            target="_blank" rel="noopener noreferrer"
            className="rounded-full p-2 text-[#2B2127] hover:bg-[#F1E3DC]"
            aria-label="Share on Pinterest"
          >
            <PinterestIcon size={14} />
          </a>
          <button onClick={copy} className="rounded-full p-2 text-[#2B2127] hover:bg-[#F1E3DC]" aria-label="Copy link" data-testid="copy-link">
            {copied ? <Check size={14} className="text-[#9D5C6C]" /> : <LinkIcon size={14} />}
          </button>
        </div>
      )}
    </div>
  );
}
